import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { X, UserPlus, Loader2, AlertCircle } from 'lucide-react';
import { Player } from '../types';
import { teamService } from '../services/teamService';
import { NATIONALITIES } from '../constants/nationalities';

const POSITION_OPTIONS = [
  'Goalkeeper',
  'Defender',
  'Midfield',
  'Winger',
  'Forward',
] as const;

type CreatePlayerFormData = {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  position: string;
  fanNumber?: string;
  nationality?: string;
};

interface CreatePlayerModalProps {
  teamId: string;
  onClose: () => void;
  onPlayerCreated: (player: Player) => void;
}

export const CreatePlayerModal: React.FC<CreatePlayerModalProps> = ({
  teamId,
  onClose,
  onPlayerCreated,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CreatePlayerFormData>();

  const onSubmit = async (data: CreatePlayerFormData) => {
    setIsLoading(true);
    setServerError('');

    try {
      const newPlayer = await teamService.createPlayer(teamId, data);
      onPlayerCreated(newPlayer);
    } catch (error: any) {
      setServerError(error.response?.data?.error?.message || 'Failed to create player');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={handleOverlayClick}
    >
      <div className="bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 pb-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center">
              <UserPlus className="w-5 h-5 text-green-700" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Add New Player</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-5">
          {/* First Name */}
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1.5">
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              id="firstName"
              type="text"
              placeholder="Enter first name"
              {...register('firstName', { required: 'First name is required' })}
              className={`w-full rounded-lg border bg-white px-3 py-2.5 text-sm min-h-[44px] focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                errors.firstName ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.firstName && (
              <p className="text-sm text-red-500 mt-1">{errors.firstName.message}</p>
            )}
          </div>

          {/* Last Name */}
          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1.5">
              Last Name <span className="text-red-500">*</span>
            </label>
            <input
              id="lastName"
              type="text"
              placeholder="Enter last name"
              {...register('lastName', { required: 'Last name is required' })}
              className={`w-full rounded-lg border bg-white px-3 py-2.5 text-sm min-h-[44px] focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                errors.lastName ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.lastName && (
              <p className="text-sm text-red-500 mt-1">{errors.lastName.message}</p>
            )}
          </div>

          {/* Date of Birth */}
          <div>
            <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700 mb-1.5">
              Date of Birth <span className="text-red-500">*</span>
            </label>
            <input
              id="dateOfBirth"
              type="date"
              {...register('dateOfBirth', { required: 'Date of birth is required' })}
              className={`w-full rounded-lg border bg-white px-3 py-2.5 text-sm min-h-[44px] focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                errors.dateOfBirth ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.dateOfBirth && (
              <p className="text-sm text-red-500 mt-1">{errors.dateOfBirth.message}</p>
            )}
          </div>

          {/* Position */}
          <div>
            <label htmlFor="position" className="block text-sm font-medium text-gray-700 mb-1.5">
              Position <span className="text-red-500">*</span>
            </label>
            <select
              id="position"
              {...register('position', { required: 'Position is required' })}
              className={`w-full rounded-lg border bg-white px-3 py-2.5 text-sm min-h-[44px] focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                errors.position ? 'border-red-300' : 'border-gray-300'
              }`}
            >
              <option value="">Select a position</option>
              {POSITION_OPTIONS.map((position) => (
                <option key={position} value={position}>
                  {position}
                </option>
              ))}
            </select>
            {errors.position && (
              <p className="text-sm text-red-500 mt-1">{errors.position.message}</p>
            )}
          </div>

          {/* FAN Number */}
          <div>
            <label htmlFor="fanNumber" className="block text-sm font-medium text-gray-700 mb-1.5">
              FAN Number <span className="text-gray-400 font-normal">(Optional)</span>
            </label>
            <input
              id="fanNumber"
              type="text"
              placeholder="Football Association Number"
              {...register('fanNumber')}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm min-h-[44px] focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>

          {/* Nationality */}
          <div>
            <label htmlFor="nationality" className="block text-sm font-medium text-gray-700 mb-1.5">
              Nationality <span className="text-gray-400 font-normal">(Optional)</span>
            </label>
            <select
              id="nationality"
              {...register('nationality')}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm min-h-[44px] focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              <option value="">Select nationality</option>
              {NATIONALITIES.map((n) => (
                <option key={n.code} value={n.code}>{n.flag} {n.name}</option>
              ))}
            </select>
          </div>

          {/* Server Error */}
          {serverError && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2.5 rounded-lg">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{serverError}</span>
            </div>
          )}

          {/* Buttons */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors min-h-[44px]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors min-h-[44px] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4" />
                  Add Player
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
